import { activityStepsPath, getActivityRuleErrors } from '@sparkapp/shared-types';
import type { ActivityPayload } from '../../types/activity';

export type ActivityFieldErrors = Record<string, string>;

/** Same single rule set as the backend: used for create, edit (any status) and Go live */
export function validateActivityPayload(payload: ActivityPayload): ActivityFieldErrors {
  return getActivityRuleErrors(payload).reduce<ActivityFieldErrors>((acc, e) => {
    acc[e.path] ??= e.message;
    return acc;
  }, {});
}

/**
 * Maps backend "path: message" strings onto form error keys.
 * steps.<index>.<field> (per-step zod errors) are folded into steps.{level} using the submitted payload.
 */
export function mapServerErrors(messages: string[], payload: ActivityPayload): ActivityFieldErrors {
  const result: ActivityFieldErrors = {};
  for (const raw of messages) {
    const sep = raw.indexOf(': ');
    if (sep === -1) {
      result._form ??= raw;
      continue;
    }
    let path = raw.slice(0, sep);
    const message = raw.slice(sep + 2);
    const stepIndex = /^steps\.(\d+)\./.exec(path);
    if (stepIndex) {
      const level = payload.steps[Number(stepIndex[1])]?.level;
      path = level ? activityStepsPath(level) : 'steps';
    }
    result[path] ??= message;
  }
  return result;
}

/** One-line toast summary, e.g. "Fix 2 issues: Description is required; Harder needs at least one step" */
export function buildActivityErrorToast(errors: ActivityFieldErrors): string {
  const messages = Object.values(errors);
  if (!messages.length) return 'Please fix the errors before saving';
  return messages.length === 1 ? messages[0] : `Fix ${messages.length} issues: ${messages.join('; ')}`;
}
